/* ============================================================
   fjör Shop Bag – Cart Drawer JS  (June 2026)
   Progress bar is fully JS-rendered (multi-currency aware).
   Data comes from data-attributes on [data-fc-progress] which
   re-render with the liquid-ajax-cart section.

   IMPORTANT: the MutationObserver watches document.body, NOT the
   [data-ajax-cart-section] element. liquid-ajax-cart can replace
   the section element itself during a re-render, which would kill
   an observer attached to it — that's exactly how the bar was
   disappearing on selling-plan changes. document.body can never
   be replaced, renders are coalesced via requestAnimationFrame,
   and an idempotency guard makes repeated calls free.
   ============================================================ */

(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────────
     Free-shipping configuration
     All amounts are MINOR units (pence / cents / öre).
     - pinned:   exact thresholds for currencies you've priced
                 deliberately. Add a line here whenever you want
                 a curated round number for a currency.
     - GBP:      split by country (UK vs International).
     - fallback: any other currency converts a GBP base via
                 Shopify's live rate (window.Shopify.currency.rate)
                 and rounds UP to the nearest 10 major units, so
                 the bar never promises free shipping below the
                 real checkout-converted threshold.
     ──────────────────────────────────────────────────────────── */
  const FS = {
    pinned: {
      EUR: 18000, // €180
      USD: 20000, // $200
    },
    gbDomestic: 10000, // £100 – GBP + country GB
    gbIntl: 20000,     // £200 – GBP + any other country
    baseEuZoneGbp: 15500, // ≈ €180 in GBP, for SEK/DKK/PLN/CZK/…
    baseIntlGbp: 20000,   // £200 base for everywhere else
    euCountries: [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
      'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    ],
  };

  const sellingPlanCache = {};
  let lineBusy = false;
  let lastProgressPct = null;
  let lastRenderKey = null;
  let renderScheduled = false;

  /* ── Drawer open / close ── */

  function cartOpen() {
    document.body.classList.add('js-my-cart-open');
  }

  function cartClose() {
    document.body.classList.remove('js-my-cart-open');
  }

  window.cartOpen = cartOpen;
  window.cartClose = cartClose;

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') cartClose();
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('.fjor-cart-backdrop')) cartClose();
  });

  /* ────────────────────────────────────────────────────────────
     Free-shipping progress bar
     ──────────────────────────────────────────────────────────── */

  function getThresholdMinor(currency, country) {
    if (currency === 'GBP') {
      return country === 'GB' ? FS.gbDomestic : FS.gbIntl;
    }

    if (FS.pinned[currency]) {
      return FS.pinned[currency];
    }

    // Fallback: convert a GBP base using Shopify's live storefront rate.
    const rate = parseFloat(window.Shopify?.currency?.rate || '');
    if (!rate || !isFinite(rate) || rate <= 0) return null; // can't compute

    const baseGbpMinor = FS.euCountries.includes(country)
      ? FS.baseEuZoneGbp
      : FS.baseIntlGbp;

    const raw = baseGbpMinor * rate;

    // Round UP to the nearest 10 major units (1000 minor units):
    // 2 437 kr → 2 440 kr. Better to slightly over-promise the
    // threshold than to under-promise and disappoint at checkout.
    return Math.ceil(raw / 1000) * 1000;
  }

  function formatMinor(minor, currency) {
    const lang = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currency,
        trailingZeroDisplay: 'stripIfInteger',
      }).format(minor / 100);
    } catch (err) {
      try {
        return new Intl.NumberFormat(lang, {
          style: 'currency',
          currency: currency,
        }).format(minor / 100);
      } catch (err2) {
        return (minor / 100).toFixed(2) + ' ' + currency;
      }
    }
  }

  function syncShippingRow(isFree) {
    const shippingEl = document.querySelector('[data-fc-shipping]');
    if (!shippingEl) return;

    if (isFree) {
      shippingEl.textContent = 'FREE';
      shippingEl.className = 'fc-breakdown-val';
    } else {
      shippingEl.textContent = 'Calculated at checkout';
      shippingEl.className = 'fc-shipping-meta';
    }
  }

  function renderProgressBar() {
    const el = document.querySelector('[data-fc-progress]');
    if (!el) {
      lastProgressPct = null;
      lastRenderKey = null;
      return;
    }

    const subtotal = parseInt(el.dataset.subtotal, 10) || 0;
    const currency = el.dataset.currency || '';
    const country = el.dataset.country || '';
    const threshold = getThresholdMinor(currency, country);

    // Idempotency guard: skip if the bar is already populated for
    // this exact cart state. This is what makes it safe to call
    // from a body-wide MutationObserver (including for mutations
    // this function causes itself) without loops or flicker.
    const key = subtotal + '|' + currency + '|' + country + '|' + threshold;
    if (key === lastRenderKey && el.firstElementChild) return;

    // Unknown currency and no usable rate → hide bar rather than lie.
    if (!threshold) {
      el.innerHTML = '';
      lastProgressPct = null;
      lastRenderKey = key;
      syncShippingRow(false);
      return;
    }

    const isFree = subtotal >= threshold;
    const pct = isFree
      ? 100
      : Math.max(4, Math.min(99, (subtotal / threshold) * 100));

    if (isFree) {
      el.innerHTML = `
        <div class="fc-progress-text is-free">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          You've got free shipping
        </div>
        <div class="fc-progress-track">
          <div class="fc-progress-fill"></div>
        </div>`;
    } else {
      const remaining = formatMinor(threshold - subtotal, currency);
      el.innerHTML = `
        <div class="fc-progress-text">
          You're <strong>${remaining}</strong> away from free shipping
        </div>
        <div class="fc-progress-track">
          <div class="fc-progress-fill"></div>
        </div>`;
    }

    syncShippingRow(isFree);

    // Animate only when the percentage actually changed; otherwise
    // (e.g. a section swap with the same subtotal) paint instantly.
    const fill = el.querySelector('.fc-progress-fill');
    const from = lastProgressPct === null ? 0 : lastProgressPct;

    if (from === pct) {
      fill.style.width = pct + '%';
    } else {
      fill.style.transition = 'none';
      fill.style.width = from + '%';
      void fill.offsetWidth; // force reflow at the old width
      fill.style.transition = ''; // restore CSS transition (0.4s ease)
      fill.style.width = pct + '%';
    }

    lastProgressPct = pct;
    lastRenderKey = key;
  }

  // Coalesce bursts of mutations into one render per frame.
  function scheduleProgressRender() {
    if (renderScheduled) return;
    renderScheduled = true;

    requestAnimationFrame(() => {
      renderScheduled = false;
      renderProgressBar();
    });
  }

  // Belt-and-braces retries for async swaps, mirroring the
  // hydrate retry pattern below.
  function renderProgressBarAfterRender() {
    scheduleProgressRender();
    setTimeout(renderProgressBar, 100);
    setTimeout(renderProgressBar, 300);
  }

  /* Body-level observer: survives ANY section/element replacement
     strategy liquid-ajax-cart uses. The rAF coalescing + render
     guard keep it effectively free. */
  function startProgressObserver() {
    if (!document.body) return;

    const observer = new MutationObserver(scheduleProgressRender);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ────────────────────────────────────────────────────────────
     Selling plans (Subscribe & Save toggle + frequency select)
     ──────────────────────────────────────────────────────────── */

  function cleanSellingPlanName(name) {
    return String(name || '')
      .replace(/^Delivery every\s*/i, '')
      .replace(/^Deliver every\s*/i, '')
      .replace(/^Subscription\s*-\s*/i, '')
      .replace(/^Subscribe and save\s*-\s*/i, '')
      .replace(/^Subscribe & save\s*-\s*/i, '')
      .trim();
  }

  async function fetchProductSellingPlans(handle) {
    if (!handle) return [];

    if (sellingPlanCache[handle]) {
      return sellingPlanCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);

      if (!response.ok) {
        throw new Error(`Product request failed: ${response.status}`);
      }

      const product = await response.json();
      const plans = [];

      (product.selling_plan_groups || []).forEach((group) => {
        (group.selling_plans || []).forEach((plan) => {
          plans.push({
            id: Number(plan.id),
            name: cleanSellingPlanName(plan.name),
          });
        });
      });

      sellingPlanCache[handle] = plans;
      return plans;
    } catch (error) {
      console.warn('fjor-cart: could not fetch selling plans for', handle, error);
      return [];
    }
  }

  async function fetchFirstSellingPlan(handle) {
    const plans = await fetchProductSellingPlans(handle);
    return plans?.[0]?.id || null;
  }

  async function hydrateFrequencySelect(select) {
    if (!select) return;

    const item = select.closest('.fc-item');
    const toggle = item?.querySelector('[data-fc-toggle]');
    const handle = toggle?.dataset.productHandle;

    if (!handle) return;

    const selectedValue = Number(select.value);
    const plans = await fetchProductSellingPlans(handle);

    if (!plans.length) return;

    const existingValues = Array.from(select.options).map((option) => Number(option.value));
    const alreadyHydrated =
      existingValues.length === plans.length &&
      plans.every((plan) => existingValues.includes(Number(plan.id)));

    if (alreadyHydrated) return;

    select.innerHTML = '';

    plans.forEach((plan) => {
      const option = document.createElement('option');
      option.value = String(plan.id);
      option.textContent = plan.name || `Plan ${plan.id}`;

      if (Number(plan.id) === selectedValue) {
        option.selected = true;
      }

      select.appendChild(option);
    });
  }

  function hydrateFrequencySelects() {
    document.querySelectorAll('[data-fc-freq]').forEach((select) => {
      hydrateFrequencySelect(select);
    });
  }

  function hydrateFrequencySelectsAfterRender() {
    requestAnimationFrame(() => {
      hydrateFrequencySelects();

      setTimeout(hydrateFrequencySelects, 100);
      setTimeout(hydrateFrequencySelects, 300);
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-fc-toggle]');
    if (!btn) return;

    if (!window.liquidAjaxCart) {
      console.warn('fjor-cart: liquidAjaxCart not ready');
      return;
    }

    if (lineBusy) return;
    lineBusy = true;

    const isSub = btn.dataset.isSub === 'true';
    const itemKey = btn.dataset.itemKey;
    const handle = btn.dataset.productHandle;

    const done = () => {
      lineBusy = false;
      hydrateFrequencySelectsAfterRender();
      renderProgressBarAfterRender();
    };

    if (isSub) {
      window.liquidAjaxCart.cartRequestChange(
        { id: itemKey, selling_plan: null },
        { newQueue: true, lastComplete: done }
      );
    } else {
      fetchFirstSellingPlan(handle)
        .then((planId) => {
          if (!planId) {
            console.warn('fjor-cart: no selling plan found for', handle);
            done();
            return;
          }

          window.liquidAjaxCart.cartRequestChange(
            { id: itemKey, selling_plan: planId },
            { newQueue: true, lastComplete: done }
          );
        })
        .catch(done);
    }
  });

  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-fc-freq]');
    if (!sel) return;

    if (!window.liquidAjaxCart) {
      console.warn('fjor-cart: liquidAjaxCart not ready');
      return;
    }

    if (lineBusy) return;
    lineBusy = true;

    const planId = Number(sel.value);

    if (!planId) {
      lineBusy = false;
      return;
    }

    window.liquidAjaxCart.cartRequestChange(
      {
        id: sel.dataset.itemKey,
        selling_plan: planId,
      },
      {
        newQueue: true,
        lastComplete: () => {
          lineBusy = false;
          hydrateFrequencySelectsAfterRender();
          renderProgressBarAfterRender();
        },
      }
    );
  });

  /* ── Lifecycle ──
     The body observer is the primary mechanism; events and the
     lastComplete callbacks above are fast-path triggers. */

  document.addEventListener('liquid-ajax-cart:sections-rendered', renderProgressBarAfterRender);
  document.addEventListener('liquid-ajax-cart:request-end', renderProgressBarAfterRender);

  function init() {
    startProgressObserver();
    renderProgressBar();
    hydrateFrequencySelectsAfterRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();